import type { AxiosInstance } from 'axios';
import {
  getStoredCredentials,
  isAccessTokenValid,
  isRefreshTokenValid,
  refreshToken,
  clearAuthStorage,
} from '../keycloakAuthService';
import {
  setStoredCredentials,
  type Credentials as StoredCredentials,
} from '../../utils/secureAuthStorage';
import { ENV } from '../../config/env';

/**
 * When the auth guard is disabled (VITE_AUTH_GUARD=false — local/testing and
 * Playwright visual-regression baseline capture), a 401 from a background API
 * call must NOT hard-navigate to /login: that defeats the router-level bypass
 * and prevents authenticated pages from rendering without a live backend.
 */
const redirectToLogin = (): void => {
  if (!ENV.AUTH_GUARD) {
    return;
  }
  if (typeof window !== 'undefined') {
    window.location.href = '/login';
  }
};

/**
 * Attach the auth interceptors (request: token attach + refresh queue;
 * response: 401 handling + refresh + retry) to the given axios instance.
 *
 * Called once at module load from `apiClient.ts` against the shared
 * `apiClient` instance. Interceptors are attached to the passed-in `client`
 * only — no global axios state is mutated, so the auth-bootstrap services
 * (`keycloakAuthService`, `keycloakDirectAuth`) can keep using default `axios` without
 * accidentally triggering refresh-on-refresh loops.
 *
 * Webapp auth contract notes:
 *   - Credentials live in AES-256-GCM-encrypted localStorage via
 *     `secureAuthStorage`. `getStoredCredentials` / `isAccessTokenValid` /
 *     `isRefreshTokenValid` from `keycloakAuthService` are therefore ALL async.
 *   - On terminal auth failure (refresh token expired, refresh call failed)
 *     we `clearAuthStorage()` and hard-navigate to `/login`.
 *   - Requests to `/auth/login`, `/auth/refresh`, `/auth/sso` short-circuit
 *     the request interceptor (no bearer attach, no refresh).
 *   - 401s on those same auth endpoints in the response interceptor skip
 *     the retry path and bounce straight to `/login` — otherwise an expired
 *     refresh would loop 401 → refresh → 401 forever and trip the backend
 *     rate-limiter.
 */

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: string | null) => void;
  reject: (error?: unknown) => void;
}> = [];

const processQueue = (error: unknown = null, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

// Helper to store refreshed credentials securely.
// Persists to disk AND updates the in-memory cache so sync readers (session
// timer, isAccessTokenValid, …) see the new expiry immediately.
const storeRefreshedCredentials = async (newCredentials: {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  refresh_expires_in: number;
}) => {
  const now = Math.floor(Date.now() / 1000);
  const credentialsWithExpiry = {
    ...newCredentials,
    access_token_expires_at: now + newCredentials.expires_in,
    refresh_token_expires_at: now + newCredentials.refresh_expires_in,
  };
  await setStoredCredentials(credentialsWithExpiry as unknown as StoredCredentials);
  return credentialsWithExpiry;
};

export function attachAuthInterceptors(client: AxiosInstance): void {
  // ============================================
  // Request Interceptor
  // ============================================
  client.interceptors.request.use(
    async (config) => {
      // Skip auth refresh logic for auth endpoints themselves
      if (
        config.url?.includes('/auth/login') ||
        config.url?.includes('/auth/refresh') ||
        config.url?.includes('/auth/sso')
      ) {
        return config;
      }

      const credentials = await getStoredCredentials();

      if (!credentials) {
        return config;
      }

      // Access token is still valid — use it directly
      if (await isAccessTokenValid()) {
        // eslint-disable-next-line require-atomic-updates
        config.headers.Authorization = `Bearer ${credentials.access_token}`;
        return config;
      }

      // Access token expired — check refresh token
      if (!(await isRefreshTokenValid())) {
        clearAuthStorage();
        redirectToLogin();
        return Promise.reject(new Error('Session expired'));
      }

      // Another refresh is already in progress — queue this request
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          config.headers.Authorization = `Bearer ${token}`;
          return config;
        });
      }

      // Perform token refresh
      isRefreshing = true;
      try {
        const response = await refreshToken(credentials.refresh_token);
        if (response.success) {
          const { credentials: newCredentials } = response.data;
          const credentialsWithExpiry = await storeRefreshedCredentials(newCredentials);
          processQueue(null, credentialsWithExpiry.access_token);

          const newAccessToken = credentialsWithExpiry.access_token;
          // eslint-disable-next-line require-atomic-updates
          config.headers.Authorization = `Bearer ${newAccessToken}`;
        }
      } catch (_error) {
        processQueue(_error, null);
        clearAuthStorage();
        redirectToLogin();
      } finally {
        // eslint-disable-next-line require-atomic-updates
        isRefreshing = false;
      }

      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // ============================================
  // Response Interceptor (Handle 401 errors)
  // ============================================
  client.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config ?? {};
      const requestUrl: string = originalRequest.url ?? '';

      // Never try to "refresh" the refresh endpoint itself, or the login
      // endpoint, or the logout endpoint — that path causes an infinite
      // 401 → refresh → 401 → refresh loop when the refresh token has
      // been revoked/expired, which gets the client IP rate-limited by
      // the backend. Just clear and bounce to /login.
      const isAuthEndpoint =
        requestUrl.includes('/auth/refresh') ||
        requestUrl.includes('/auth/login') ||
        requestUrl.includes('/auth/logout') ||
        requestUrl.includes('/auth/sso');

      if (error.response?.status === 401 && isAuthEndpoint) {
        // Already on /login? don't re-redirect — avoids reload loops if the
        // login page itself somehow surfaces a 401.
        if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
          clearAuthStorage();
          redirectToLogin();
        }
        return Promise.reject(error);
      }

      if (error.response?.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;

        const credentials = await getStoredCredentials();

        if (credentials && (await isRefreshTokenValid())) {
          try {
            const response = await refreshToken(credentials.refresh_token);
            if (response.success) {
              const { credentials: newCredentials } = response.data;
              const credentialsWithExpiry = await storeRefreshedCredentials(newCredentials);

              originalRequest.headers.Authorization = `Bearer ${credentialsWithExpiry.access_token}`;
              return client(originalRequest);
            }
          } catch (refreshError) {
            clearAuthStorage();
            redirectToLogin();
            return Promise.reject(refreshError);
          }
        } else {
          clearAuthStorage();
          if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
            redirectToLogin();
          }
        }
      }

      return Promise.reject(error);
    }
  );
}
